import type { AuthUserDto } from '../generated/contracts';
import { el } from './portal/dom';

// The same two stylesheets the portal shell loads, imported here rather than relied on: this
// screen is mounted by `AccountGate` before any portal route exists, and a page whose styling
// arrives only because some other module happened to be in the bundle is a page one code-split
// away from rendering unstyled. Every rule it uses is scoped under `.tunet-surface`, which is why
// it can share a document with the VR client's own `:root` (see `scoping.test.ts`).
import '../../../shared-web/styles.css';
import './portal/signin.css';

/**
 * The sign-in screen. **Google is the way in, and there is no other control on the page.**
 *
 * ================================================================================
 * PASSWORD SIGN-IN STILL EXISTS AND DELIBERATELY HAS NO USER INTERFACE.
 * ================================================================================
 *
 * `POST /api/auth/login` is still there, still real, and still what `npm start`'s readiness
 * probe, `npm run demo`, hub-smoke and the whole backend suite authenticate through — CLAUDE.md's
 * *"the tools sign in for real; there is no bypass"* is untouched. What it does not have any more
 * is a form. The owner: *"We can keep the password login as a feature with no user interface"*,
 * and *"password logins go dark the second I'm in"*.
 *
 * **Removing the form is not what disables it, and nothing here should be read as if it were.**
 * The control is `TUNET_PASSWORD_LOGIN_ENABLED`, checked inside the route itself, because a
 * client that simply never renders a form stops nobody who can type `curl`
 * (plan/auth-and-org-admission.md, "Correction nine": hiding a UI is not a control). This file
 * removing the form is a decision about what the product looks like; the endpoint's own flag is
 * the decision about what it permits.
 *
 * **This screen does not resolve — it navigates.** Every other screen in this client settles
 * in place. A Google sign-in is a full-page navigation to `accounts.google.com` and back to
 * `/signin-google`, so `waitForSignIn()`'s promise is never kept: the document is replaced. That
 * is why the button is an ordinary `<a href>` rather than a fetch, and why
 * {@link rememberDestination} exists — see its own remarks for the deep-link problem the round
 * trip creates and the deliberately narrow thing done about it.
 *
 * **The tone has to match the server's own refusal page.** `GoogleSignIn.OnRemoteFailure` answers
 * a 403 saying Tunet is invite-only and that *nothing was emailed to anyone*. This page says the
 * same thing in fewer words. Two surfaces disagreeing about whether somebody should expect an
 * email is the one contradiction a person hitting the refusal would actually notice.
 */
export class SignIn {
  private readonly root: HTMLElement;

  /**
   * @param authOrigin
   *   Where the Google handshake starts, for a document that is not served by the host that
   *   owns it. Empty — the default, and what the web app passes — makes the link same-origin,
   *   exactly as it always was.
   *
   *   **This exists because a callback path is a value in somebody else's console.**
   *   `GoogleSignIn.CallbackPath` is registered against one hostname; the application builds
   *   its `redirect_uri` from the request it received, so a challenge started from
   *   `vr.tunet.dev` would ask Google to come back to a URI that is not registered and be
   *   refused with `redirect_uri_mismatch`. Starting it on the web host instead keeps one
   *   callback URL, on one host, forever — which is also why the VR host issues no session of
   *   its own (plan: it never signs anybody in).
   */
  constructor(host: HTMLElement, authOrigin = '') {
    const signInLink = el('a', {
      class: 'btn btn-primary auth-google',
      href: `${authOrigin}/auth/google`,
      'data-google-sign-in': 'true',
      text: 'Continue with Google',
    });

    signInLink.addEventListener('click', () => rememberDestination());

    this.root = el('div', { class: 'tunet-surface auth-layer' }, [
      el('main', { class: 'auth-card' }, [
        el('div', { class: 'auth-wordmark' }, [
          el('strong', { text: 'Tunet' }),
          el('span', { text: 'say TOO-net' }),
        ]),

        // The one line this page keeps. It says what the product is, which the wordmark does
        // not, and it is the only sentence here — plan/portal-design.md's second rule applies to
        // a sign-in screen exactly as it does to a dashboard.
        el('p', { class: 'auth-lede', text: 'A room you meet people in, and draw in.' }),

        signInLink,

        // Honest and short, and it matches GoogleSignIn's own 403 page rather than restating it.
        // "Nothing is emailed" is not a caveat here, it is the instruction: somebody waiting for
        // a message that will never arrive is the failure this one line prevents.
        el('p', { class: 'auth-foot', text: 'Invite only. Ask whoever invited you — nothing is emailed.' }),
      ]),
    ]);

    host.appendChild(this.root);
  }

  /**
   * Resolves once signed in — which, on this screen, never happens: the Google handshake
   * replaces the document rather than returning a value. Kept because `AccountGate` and
   * `main.ts` are written against it and the shape is still the right one for a screen that
   * blocks a route; a future in-page provider would keep this contract unchanged.
   */
  waitForSignIn(): Promise<AuthUserDto> {
    return new Promise(() => {});
  }

  dispose(): void {
    this.root.remove();
  }
}

/** Where a pre-sign-in destination is parked across the Google round trip. */
export const DESTINATION_KEY = 'tunet.signin.destination';

/**
 * Parks the path the visitor actually asked for, so the round trip does not silently land every
 * deep link on the portal home.
 *
 * **The problem is real and is created by the redirect.** Sign-in used to settle in place, so
 * `main.ts`'s `handleUnauthorized` could simply retry the exact path that was refused — the URL
 * never changed. A full-page navigation destroys that: the browser comes back to
 * `GoogleSignIn.SignedInDestination`, a fixed `/`, and `/room?roomId=…` is gone.
 *
 * **Why this is not a `returnUrl`.** The server deliberately refuses to take a caller-supplied
 * destination — that is the admin surface's own precedent, and an open redirect is not worth the
 * convenience. So the path never leaves this browser: it is written to `sessionStorage` here and
 * read back by `main.ts` after the session exists. The server is never told, and never has to
 * decide whether to trust anything.
 *
 * **Validated on the way back out, not on the way in** ({@link takeRememberedDestination}).
 * `sessionStorage` is same-origin, but it is still storage a page's own scripts can write, so the
 * value is treated as untrusted at the point it would be acted on rather than at the point it was
 * saved. Anything that is not one of this app's own absolute in-app paths is discarded.
 */
export function rememberDestination(): void {
  try {
    window.sessionStorage.setItem(
      DESTINATION_KEY,
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    // Private mode, a full quota, a browser with storage disabled. Losing the deep link is a
    // worse landing page, not a broken sign-in, so this must never throw into the click handler.
  }
}

/**
 * The parked destination, consumed — or `null` when there is none or it is not one of this app's
 * own paths.
 *
 * The allowlist is `/portal`, `/room` and `/diagram` and nothing else, matched as whole segments:
 * `//evil.com` is a protocol-relative URL that `location.assign` would treat as another origin,
 * and `/portalx` is not a route. Both are rejected by construction rather than by a `startsWith`
 * that happens to be tight enough today.
 *
 * `/diagram` (plan/diagram-extraction.md, A5) is the case that makes the deep-link parking worth
 * having at all: a diagram link is the one URL somebody genuinely *sends to another person*, and
 * that person is exactly the one most likely not to have a session yet. Without it here, every
 * shared link would land the recipient on the portal home after signing in, with nothing to say
 * which diagram they were sent.
 */
export function takeRememberedDestination(): string | null {
  // Declared without an initialiser: the `catch` returns rather than falling through, so the
  // `= null` that used to be here was a value nothing could ever read. Keeping it would suggest
  // a third outcome — "storage threw, carry on with nothing" — that this function deliberately
  // does not have.
  let stored: string | null;

  try {
    stored = window.sessionStorage.getItem(DESTINATION_KEY);
    window.sessionStorage.removeItem(DESTINATION_KEY);
  } catch {
    // Storage unavailable (private mode, a headset browser with it disabled) is indistinguishable
    // from nothing parked, and both mean the same thing to every caller: no destination.
    return null;
  }

  if (stored === null) {
    return null;
  }

  const path = stored.split('?')[0] ?? '';

  return path === '/portal' || path === '/room' || path === '/diagram' || path.startsWith('/portal/')
    ? stored
    : null;
}
